import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus, Search, Pencil, Trash2, Power, Bike, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/services/api";
import { fmtDate, fmtMoney } from "@/utils/format";
import { useSingleFlightMutation } from "@/hooks/useSingleFlightMutation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const EMPTY = { name: "", phone: "", notes: "", active: true, bot_enabled: false, pin: "", default_agent_id: "" };

export default function MesajePage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [dispatch, setDispatch] = useState({
    dest_kind: "agent",
    agent_id: "",
    mesaje_id: "",
    delivery_mesaje_id: "",
    task_name: "",
    amount: "",
    goud_amount: "",
    goud_rate: "",
    pickup_agent_id: "",
    pickup_address: "",
    delivery_address: "",
  });
  const [preview, setPreview] = useState("");

  const { data: mesajes = [], isLoading } = useQuery({
    queryKey: ["mesajes"],
    queryFn: async () => (await api.get("/mesajes")).data,
  });

  const { data: dispatches = [] } = useQuery({
    queryKey: ["mesaje-dispatches"],
    queryFn: async () => {
      const data = (await api.get("/mesajes/dispatches")).data;
      // Backend auto-repairs stuck credits on this call — refresh related views.
      qc.invalidateQueries({ queryKey: ["control-week"] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      return data;
    },
  });

  const { data: agents = [] } = useQuery({
    queryKey: ["agents"],
    queryFn: async () => (await api.get("/agents")).data,
  });

  const activeAgents = useMemo(
    () => agents.filter((a) => a.active !== false),
    [agents],
  );

  const activeMesajes = useMemo(
    () => mesajes.filter((m) => m.active !== false),
    [mesajes],
  );

  const mesajesForAgent = useMemo(() => {
    if (!dispatch.agent_id) return activeMesajes;
    const linked = activeMesajes.filter((m) => m.default_agent_id === dispatch.agent_id);
    const rest = activeMesajes.filter((m) => m.default_agent_id !== dispatch.agent_id);
    return [...linked, ...rest];
  }, [activeMesajes, dispatch.agent_id]);

  const filtered = useMemo(
    () => mesajes.filter((m) =>
      !search
      || (m.name || "").toLowerCase().includes(search.toLowerCase())
      || (m.phone || "").includes(search.replace(/\s/g, ""))
    ),
    [mesajes, search],
  );

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      ...EMPTY,
      ...m,
      pin: m.pin || "",
      default_agent_id: m.default_agent_id || "",
      bot_enabled: !!m.bot_enabled,
    });
    setModalOpen(true);
  };

  const saveMut = useSingleFlightMutation({
    mutationFn: async () => {
      const payload = { ...form };
      if (editing) return (await api.put(`/mesajes/${editing.id}`, payload)).data;
      return (await api.post("/mesajes", payload)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Mesajé modifye" : "Mesajé ajoute");
      qc.invalidateQueries({ queryKey: ["mesajes"] });
      setModalOpen(false);
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mesajes/${id}`)).data,
    onSuccess: () => { toast.success("Mesajé efase"); qc.invalidateQueries({ queryKey: ["mesajes"] }); },
  });

  const toggleActive = useMutation({
    mutationFn: async (m) => (await api.put(`/mesajes/${m.id}`, { active: !m.active })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mesajes"] }),
  });

  const previewMut = useMutation({
    mutationFn: async (payload) => (await api.post("/mesajes/preview-dispatch", payload)).data,
    onSuccess: (data) => setPreview(data.message || ""),
    onError: () => setPreview(""),
  });

  const pickupAgent = activeAgents.find((a) => a.id === dispatch.pickup_agent_id);
  const toMesaje = dispatch.dest_kind === "mesaje";
  const holdsGoud = toMesaje && !!pickupAgent?.holds_goud;
  const goudUsd = holdsGoud && parseFloat(dispatch.goud_rate) > 0
    ? (parseFloat(dispatch.goud_amount) || 0) / parseFloat(dispatch.goud_rate)
    : 0;

  const buildDispatchPayload = () => ({
    mesaje_id: dispatch.mesaje_id,
    agent_id: toMesaje ? "" : dispatch.agent_id,
    delivery_mesaje_id: toMesaje ? dispatch.delivery_mesaje_id : "",
    task_name: dispatch.task_name.trim(),
    amount: holdsGoud ? Number(goudUsd.toFixed(2)) : (parseFloat(dispatch.amount) || 0),
    goud_amount: holdsGoud ? (parseFloat(dispatch.goud_amount) || 0) : 0,
    goud_rate: holdsGoud ? (parseFloat(dispatch.goud_rate) || 0) : 0,
    pickup_agent_id: dispatch.pickup_agent_id || "",
    pickup_address: dispatch.pickup_address.trim(),
    delivery_address: dispatch.delivery_address.trim(),
  });

  const dispatchMut = useSingleFlightMutation({
    mutationFn: async () => (await api.post("/mesajes/dispatch", buildDispatchPayload())).data,
    onSuccess: (data) => {
      toast.success(
        toMesaje
          ? `Mesaj voye — lòt mesajé a dwe reponn wi anvan kob la soti nan men ajan an`
          : `Mesaj voye bay ${data.to || "mesajé"} — konfime lè mesajé a livre lajan bay ajan an`,
      );
      setDispatch({
        dest_kind: "agent",
        agent_id: "",
        mesaje_id: "",
        delivery_mesaje_id: "",
        task_name: "",
        amount: "",
        goud_amount: "",
        goud_rate: "",
        pickup_agent_id: "",
        pickup_address: "",
        delivery_address: "",
      });
      setPreview("");
      qc.invalidateQueries({ queryKey: ["mesaje-dispatches"] });
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const confirmMut = useSingleFlightMutation({
    mutationFn: async (id) => (await api.post(`/mesajes/dispatches/${id}/confirm`)).data,
    onSuccess: (data) => {
      if (data.status === "secured") {
        const left = data.goud_left != null ? `${Number(data.goud_left).toLocaleString()} HTG rete` : "balans ajan an mete ajou";
        toast.success(`Misyon sekirize — ${left}`);
      } else {
        const added = data.funding?.amount ?? data.amount ?? 0;
        toast.success(
          `+${fmtMoney(added)} ajoute pou ${data.agent_name} — ${fmtMoney(data.cash_in_hand ?? 0)} rete nan men`,
        );
      }
      qc.invalidateQueries({ queryKey: ["mesaje-dispatches"] });
      qc.invalidateQueries({ queryKey: ["control-week"] });
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const deleteDispatchMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mesajes/dispatches/${id}`)).data,
    onSuccess: (data) => {
      const extra = data.funding_reversed
        ? data.pickup_deduction_reversed
          ? " — balans tou de ajan + Tablo Kontwòl retabli"
          : " — balans ajan + Tablo Kontwòl retabli"
        : "";
      toast.success(`Misyon efase${extra}`);
      qc.invalidateQueries({ queryKey: ["mesaje-dispatches"] });
      if (data.funding_reversed) {
        qc.invalidateQueries({ queryKey: ["control-week"] });
        qc.invalidateQueries({ queryKey: ["agents"] });
      }
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const amountReady = holdsGoud
    ? parseFloat(dispatch.goud_amount) > 0 && parseFloat(dispatch.goud_rate) > 0
    : parseFloat(dispatch.amount) > 0;
  const canPreview = dispatch.mesaje_id
    && dispatch.task_name.trim().length >= 2
    && amountReady
    && (toMesaje
      ? dispatch.pickup_agent_id && dispatch.delivery_mesaje_id && dispatch.delivery_mesaje_id !== dispatch.mesaje_id
      : !!dispatch.agent_id);

  const dispatchStatusLabel = (status) => {
    if (status === "credited") return "Kredite";
    if (status === "secured") return "Sekirize";
    if (status === "awaiting_agent") return "Ap tann ajan";
    if (status === "awaiting_mesaje") return "Ap tann lòt mesajé";
    if (status === "sent") return "Ap tann mesajé";
    return "Ap tann";
  };

  const handlePreview = () => {
    if (!canPreview) {
      setPreview("");
      return;
    }
    previewMut.mutate(buildDispatchPayload());
  };

  const onAgentChange = (agentId) => {
    const agent = activeAgents.find((a) => a.id === agentId);
    setDispatch((prev) => {
      const mesaje = activeMesajes.find((m) => m.id === prev.mesaje_id);
      const keepMesaje = mesaje && (!mesaje.default_agent_id || mesaje.default_agent_id === agentId);
      const pickupAgentId = prev.pickup_agent_id === agentId ? "" : prev.pickup_agent_id;
      return {
        ...prev,
        agent_id: agentId,
        mesaje_id: keepMesaje ? prev.mesaje_id : "",
        pickup_agent_id: pickupAgentId,
        delivery_address: agent?.address?.trim() || prev.delivery_address,
      };
    });
  };

  const onPickupAgentChange = (pickupAgentId) => {
    const pickupAgent = activeAgents.find((a) => a.id === pickupAgentId);
    setDispatch((prev) => ({
      ...prev,
      pickup_agent_id: pickupAgentId,
      pickup_address: pickupAgent?.address?.trim() || prev.pickup_address,
      goud_rate: pickupAgent?.holds_goud ? String(pickupAgent.goud_rate || prev.goud_rate || "") : prev.goud_rate,
    }));
  };

  const onMesajeChange = (mesajeId) => {
    const mesaje = activeMesajes.find((m) => m.id === mesajeId);
    const agent = mesaje?.default_agent_id
      ? activeAgents.find((a) => a.id === mesaje.default_agent_id)
      : null;
    setDispatch((prev) => ({
      ...prev,
      mesaje_id: mesajeId,
      agent_id: prev.dest_kind === "mesaje" ? "" : (agent?.id || prev.agent_id),
      delivery_address: prev.dest_kind === "mesaje"
        ? prev.delivery_address
        : (agent?.address?.trim() || prev.delivery_address),
    }));
  };

  const regeneratePin = () => {
    const pin = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
    setForm((f) => ({ ...f, pin, bot_enabled: true }));
  };

  return (
    <div className="space-y-8">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <Bike className="h-8 w-8 text-brand-blue" /> Mesajé
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ajoute mesajé yo epi voye yo yon mesaj otomatik — pran lajan nan yon adrès, pote li nan yon lòt.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-mesaje-btn" className="bg-brand-blue hover:bg-brand-blueHover text-white gap-2">
          <Plus className="h-4 w-4" /> Ajoute mesajé
        </Button>
      </motion.div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Send className="h-5 w-5 text-brand-gold" /> Voye misyon bay mesajé
        </h2>
          <p className="text-xs text-muted-foreground">
            Menm demach sekirite a: mesajé a konfime li remèt kob la, epi moun k ap resevwa a reponn *wi* apre li verifye fizikman.
            Lè w ap pran kob nan men ajan Goud la pou pote bay yon lòt mesajé, kob la soti nan balans goud la sèlman apre *wi* a.
          </p>
          <div className="grid lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div>
              <Label>Kote pou pote kob la</Label>
              <Select
                value={dispatch.dest_kind}
                onValueChange={(v) => setDispatch((prev) => ({
                  ...prev,
                  dest_kind: v,
                  agent_id: v === "mesaje" ? "" : prev.agent_id,
                  delivery_mesaje_id: v === "agent" ? "" : prev.delivery_mesaje_id,
                }))}
              >
                <SelectTrigger data-testid="dispatch-dest-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Bay yon ajan</SelectItem>
                  <SelectItem value="mesaje">Bay yon lòt mesajé (ajan Goud / sekirite)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!toMesaje && (
            <div>
              <Label>Ajan (k ap resevwa lajan) *</Label>
              <Select
                value={dispatch.agent_id || "__none__"}
                onValueChange={(v) => onAgentChange(v === "__none__" ? "" : v)}
              >
                <SelectTrigger data-testid="dispatch-agent-select">
                  <SelectValue placeholder="Chwazi ajan an premye" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Chwazi ajan…</SelectItem>
                  {activeAgents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <Label>{toMesaje ? "Mesajé k ap pran kob la *" : "Mesajé"}</Label>
              <Select
                value={dispatch.mesaje_id || "__none__"}
                onValueChange={(v) => onMesajeChange(v === "__none__" ? "" : v)}
                disabled={!toMesaje && !dispatch.agent_id}
              >
                <SelectTrigger data-testid="dispatch-mesaje-select">
                  <SelectValue placeholder={toMesaje || dispatch.agent_id ? "Chwazi mesajé" : "Chwazi ajan an premye"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Chwazi…</SelectItem>
                  {mesajesForAgent.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name} — {m.phone}
                      {m.default_agent_id === dispatch.agent_id ? " ★" : ""}
                      {m.bot_enabled ? " 🤖" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!toMesaje && !dispatch.agent_id && (
                <p className="text-xs text-muted-foreground mt-1">Chwazi ajan an anvan mesajé a.</p>
              )}
            </div>
            {toMesaje && (
              <div>
                <Label>Mesajé k ap resevwa kob la *</Label>
                <Select
                  value={dispatch.delivery_mesaje_id || "__none__"}
                  onValueChange={(v) => {
                    const id = v === "__none__" ? "" : v;
                    const recv = activeMesajes.find((m) => m.id === id);
                    setDispatch((prev) => ({
                      ...prev,
                      delivery_mesaje_id: id,
                      delivery_address: recv?.name || prev.delivery_address,
                    }));
                  }}
                >
                  <SelectTrigger data-testid="dispatch-delivery-mesaje-select">
                    <SelectValue placeholder="Chwazi lòt mesajé a" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Chwazi…</SelectItem>
                    {activeMesajes
                      .filter((m) => m.id !== dispatch.mesaje_id)
                      .map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name} — {m.phone}{m.bot_enabled ? " 🤖" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Li resevwa mesaj pou reponn wi apre li konte kob la. Tou de mesajé yo dwe gen bot aktif.
                </p>
              </div>
            )}
            <div>
              <Label>Non tach / misyon *</Label>
              <Input
                value={dispatch.task_name}
                onChange={(e) => setDispatch({ ...dispatch, task_name: e.target.value })}
                placeholder="egz. Bay lajan biwo, Pran kob Delmas…"
                data-testid="dispatch-task-name"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Mesajé a wè non tach la ak yon nimewo misyon 5 chif otomatik.
              </p>
            </div>
            {holdsGoud ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Kob pou al chèche (HTG) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={dispatch.goud_amount}
                    onChange={(e) => setDispatch({ ...dispatch, goud_amount: e.target.value })}
                    placeholder="egz. 13200"
                    data-testid="dispatch-goud-amount"
                  />
                </div>
                <div>
                  <Label>To jodi a (HTG pou $1) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={dispatch.goud_rate}
                    onChange={(e) => setDispatch({ ...dispatch, goud_rate: e.target.value })}
                    placeholder="egz. 132"
                    data-testid="dispatch-goud-rate"
                  />
                </div>
                <p className="sm:col-span-2 text-xs text-muted-foreground">
                  Ekwivalan: {goudUsd > 0 ? `$${goudUsd.toFixed(2)} USD` : "—"}. Goud la soti nan balans ajan an sèlman lè lòt mesajé a reponn wi.
                </p>
              </div>
            ) : (
            <div>
              <Label>Konbyen kob mesajé a dwe al chèche (USD) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={dispatch.amount}
                onChange={(e) => setDispatch({ ...dispatch, amount: e.target.value })}
                placeholder="egz. 500"
                data-testid="dispatch-amount"
              />
            </div>
            )}
            <div>
              <Label>{toMesaje ? "Ajan k ap bay kob la *" : "Pran nan men ajan (opsyonèl)"}</Label>
              <Select
                value={dispatch.pickup_agent_id || "__none__"}
                onValueChange={(v) => onPickupAgentChange(v === "__none__" ? "" : v)}
                disabled={!toMesaje && !dispatch.agent_id}
              >
                <SelectTrigger data-testid="dispatch-pickup-agent-select">
                  <SelectValue placeholder={toMesaje ? "Chwazi ajan Goud oswa lòt ajan" : "Pa nan men ajan — lòt sous"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{toMesaje ? "Chwazi ajan…" : "Pa nan men ajan (lòt sous)"}</SelectItem>
                  {activeAgents
                    .filter((a) => a.id !== dispatch.agent_id)
                    .map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}{a.holds_goud ? ` — Goud · to ${a.goud_rate}` : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {toMesaje
                  ? "Ajan an resevwa mesaj. Kob la soti nan balans li sèlman lè mesajé k ap resevwa a reponn wi."
                  : "Si ou chwazi yon ajan, li resevwa mesaj — lè livrezon fini montan an soti nan balans li."}
              </p>
            </div>
            <div>
              <Label>Adrès pou pran lajan (mesajé sèlman)</Label>
              <Textarea
                rows={2}
                value={dispatch.pickup_address}
                onChange={(e) => setDispatch({ ...dispatch, pickup_address: e.target.value })}
                placeholder={dispatch.pickup_agent_id ? "Ranpli otomatik lè w chwazi ajan sous la" : "Opsyonèl — mesajé wè adrès la"}
                data-testid="dispatch-pickup"
              />
            </div>
            <div>
              <Label>{toMesaje ? "Adrès pou remèt lòt mesajé a" : "Adrès livrezon bay ajan (mesajé sèlman)"}</Label>
              <Textarea
                rows={2}
                value={dispatch.delivery_address}
                onChange={(e) => setDispatch({ ...dispatch, delivery_address: e.target.value })}
                placeholder="Opsyonèl — ranpli otomatik lè w chwazi ajan resevwa a"
                data-testid="dispatch-delivery"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={handlePreview} disabled={!canPreview || previewMut.isPending}>
                Gade mesaj la
              </Button>
              <Button
                type="button"
                className="bg-brand-gold hover:bg-brand-goldHover text-slate-900 font-semibold gap-2"
                disabled={!canPreview || dispatchMut.isPending}
                onClick={() => dispatchMut.mutate()}
                data-testid="dispatch-send-btn"
              >
                <Send className="h-4 w-4" />
                {dispatchMut.isPending ? "Ap voye…" : "Voye sou WhatsApp"}
              </Button>
            </div>
          </div>
          <div>
            <Label>Mesaj otomatik (ap voye bay mesajé a)</Label>
            <Textarea
              rows={14}
              readOnly
              value={preview}
              placeholder="Chwazi mesajé + montan, epi klike « Gade mesaj la »"
              className="font-mono text-sm bg-secondary/30 mt-1"
              data-testid="dispatch-preview"
            />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Chèche…" className="pl-9 h-9" />
          </div>
          <div className="text-xs text-muted-foreground">{filtered.length} mesajé</div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Non</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Bot</TableHead>
                <TableHead>Ajan default</TableHead>
                <TableHead>Nòt</TableHead>
                <TableHead>Estati</TableHead>
                <TableHead className="text-right">Aksyon</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && [...Array(2)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-8" /></TableCell></TableRow>
              ))}
              {!isLoading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Pa gen mesajé — ajoute youn</TableCell></TableRow>
              )}
              {filtered.map((m) => (
                <TableRow key={m.id} data-testid={`mesaje-row-${m.id}`}>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell className="font-mono text-sm">{m.phone}</TableCell>
                  <TableCell>
                    <Badge variant={m.bot_enabled ? "default" : "outline"} className={m.bot_enabled ? "bg-sky-500/20 text-sky-600 border-sky-500/30" : ""}>
                      {m.bot_enabled ? "Aktif" : "Off"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{m.default_agent_name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[160px] truncate">{m.notes || "—"}</TableCell>
                  <TableCell>
                    <Badge variant={m.active !== false ? "default" : "outline"} className={m.active !== false ? "bg-emerald-500/20 text-emerald-500 border-emerald-500/30" : ""}>
                      {m.active !== false ? "Aktif" : "Inaktif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => toggleActive.mutate(m)}><Power className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(m)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => window.confirm("Efase?") && deleteMut.mutate(m.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {dispatches.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-4">
          <h3 className="font-heading font-semibold mb-3">Dènye misyon voye</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto text-sm">
            {dispatches.slice(0, 10).map((d) => (
              <div key={d.id} className="rounded-xl border border-border p-3 bg-secondary/20 flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {d.mission_no && (
                      <span className="font-mono text-brand-gold">#{d.mission_no}</span>
                    )}
                    {d.task_name || "Misyon"} — {d.mesaje_name} → {d.delivery_mesaje_name || d.agent_name}
                    <Badge
                      variant={d.status === "credited" ? "default" : "outline"}
                      className={
                        d.status === "credited" || d.status === "secured"
                          ? "bg-emerald-500/20 text-emerald-600 border-emerald-500/30"
                          : d.status === "awaiting_agent" || d.status === "awaiting_mesaje"
                            ? "bg-amber-500/20 text-amber-700 border-amber-500/30"
                            : ""
                      }
                    >
                      {dispatchStatusLabel(d.status)}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {d.goud_amount > 0
                      ? `${Number(d.goud_amount).toLocaleString()} HTG · to ${d.goud_rate} · ${fmtMoney(d.amount)}`
                      : fmtMoney(d.amount)}
                    {" · "}{fmtDate(d.created_at)}
                  </div>
                  {d.pickup_agent_name && (
                    <div className="text-xs text-muted-foreground">Sous: ajan {d.pickup_agent_name}</div>
                  )}
                  {(d.pickup_address || d.delivery_address) && (
                    <>
                      {d.pickup_address && (
                        <div className="text-xs text-muted-foreground">Pran: {d.pickup_address}</div>
                      )}
                      {d.delivery_address && (
                        <div className="text-xs text-muted-foreground">Pote: {d.delivery_address}</div>
                      )}
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {d.status !== "credited" && d.status !== "secured" && (
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                      disabled={confirmMut.isPending}
                      onClick={() => confirmMut.mutate(d.id)}
                      data-testid={`confirm-dispatch-${d.id}`}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {d.delivery_mesaje_id ? "Konfime sekirite" : "Konfime & kredite"}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-rose-600 border-rose-500/40 hover:bg-rose-500/10 gap-1"
                    disabled={deleteDispatchMut.isPending}
                    onClick={() => {
                      const warn = d.status === "credited"
                        ? `Efase misyon #${d.mission_no || ""}? Balans ajan${d.pickup_agent_name ? " yo" : ""} (${fmtMoney(d.amount)}) ak antre Tablo Kontwòl ap retabli otomatikman.`
                        : `Efase misyon #${d.mission_no || ""}?`;
                      if (window.confirm(warn)) deleteDispatchMut.mutate(d.id);
                    }}
                    data-testid={`delete-dispatch-${d.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                    Efase
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifye mesajé" : "Ajoute mesajé"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label>Non</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mesaje-form-name" />
            </div>
            <div>
              <Label>WhatsApp</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+509… / +1…" className="font-mono" data-testid="mesaje-form-phone" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <div>
                <span className="text-sm font-medium">Bot Mesajé (WhatsApp)</span>
                <p className="text-xs text-muted-foreground">Mesajé a ka tape *misyon* pou wè travay yo</p>
              </div>
              <Switch checked={!!form.bot_enabled} onCheckedChange={(v) => setForm({ ...form, bot_enabled: v, pin: v && !form.pin ? String(Math.floor(Math.random() * 10000)).padStart(4, "0") : form.pin })} />
            </div>
            {form.bot_enabled && (
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>PIN bot (4 chif)</Label>
                  <Input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="font-mono" data-testid="mesaje-form-pin" />
                </div>
                <Button type="button" variant="outline" onClick={regeneratePin}>Nouvo PIN</Button>
              </div>
            )}
            <div>
              <Label>Ajan default (opsyonèl)</Label>
              <Select
                value={form.default_agent_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, default_agent_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="mesaje-form-default-agent">
                  <SelectValue placeholder="Pa gen default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Pa gen default</SelectItem>
                  {activeAgents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nòt (opsyonèl)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <span className="text-sm font-medium">Aktif</span>
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Anile</Button>
            <Button className="bg-brand-blue hover:bg-brand-blueHover text-white" disabled={!form.name.trim() || !form.phone.trim() || saveMut.isPending} onClick={() => saveMut.mutate()} data-testid="mesaje-form-save">
              Sove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
